import * as THREE from 'three';
import { MechanicSchema } from '@/data/mechanic';
import { getComponentForSchema } from '@/data/registry';
import type { SelectableObject } from '@/three/selectableObject';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

type SelectedObjectFieldsProps = {
  object: SelectableObject;
};

function toLabel(field: string) {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, value => value.toUpperCase());
}

export function SelectedObjectFields({ object }: SelectedObjectFieldsProps) {
  const { schema } = object;
  const [encoded, setEncoded] = useState(() =>
    JSON.stringify(MechanicSchema.encode(object.encode()), null, 2),
  );
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(encoded);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  }, [encoded]);

  useEffect(() => {
    const off = object.onAny((key, value) => {
      setEncoded(
        JSON.stringify(MechanicSchema.encode(object.encode()), null, 2),
      );
    });

    return () => {
      off(); // Cleanup listener on component unmount
    };
  }, [object]);

  const handleRotate = useCallback(
    (dir: number) => {
      const quaternion = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 1, 0),
        (dir * Math.PI) / 2,
      );
      const newQuaternion = object.quaternion.clone().multiply(quaternion);
      object.setQuaternion(
        newQuaternion.x,
        newQuaternion.y,
        newQuaternion.z,
        newQuaternion.w,
      );
    },
    [object],
  );

  return (
    <>
      <div className="mb-2 flex items-center gap-1 text-sm text-white/80">
        <span>Rotation</span>
        <button
          className="cursor-pointer rounded bg-white/20 px-2 py-1 text-xs"
          onClick={() => handleRotate(-1)}
        >
          <Icon icon="mdi:rotate-left" />
        </button>
        <button
          className="cursor-pointer rounded bg-white/20 px-2 py-1 text-xs"
          onClick={() => handleRotate(1)}
        >
          <Icon icon="mdi:rotate-right" />
        </button>
      </div>
      {Object.entries(schema.shape).map(([field, fieldSchema]) => {
        const FieldComponent = getComponentForSchema(fieldSchema);
        if (!FieldComponent) {
          return null;
        }

        return (
          <FieldComponent
            key={field}
            object={object}
            field={field}
            label={toLabel(field)}
          />
        );
      })}

      <details className="relative mt-4 flex min-h-0 flex-col open:details-content:contents">
        <summary className="cursor-pointer text-sm text-white/80">
          Object JSON
        </summary>
        <div className="scrollbar relative z-0 min-h-0 overflow-auto rounded-md rounded-r-sm bg-black/20 contain-content">
          <div className="sticky top-0 mr-auto h-0 w-full">
            <button
              className="absolute top-2 right-2 cursor-pointer rounded bg-white/20 px-2 py-1 text-xs text-white hover:bg-white/30"
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              marginBlock: 0,
              backgroundColor: 'transparent',
            }}
          >
            {encoded}
          </SyntaxHighlighter>
        </div>
      </details>
    </>
  );
}
